import { resolveMediaUrl } from '../../../utils/helpers';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Users, Zap, ChevronDown, Plus, X, ArrowRight,
  Check, AlertCircle, RefreshCw,
} from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { academyAPI } from '../../../services/api';
import toast from 'react-hot-toast';

// ── Helpers ────────────────────────────────────────────────────────────────────

function calcAge(dob) {
  if (!dob) return null;
  return Math.floor((Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
}

function getAgeGroup(dob) {
  const age = calcAge(dob);
  if (age === null) return null;
  if (age < 6)  return 'U6';
  if (age < 8)  return 'U8';
  if (age < 10) return 'U10';
  if (age < 12) return 'U12';
  if (age < 14) return 'U14';
  if (age < 16) return 'U16';
  if (age < 18) return 'U18';
  return 'Adult';
}

const AGE_GROUPS   = ['U6','U8','U10','U12','U14','U16','U18','Adult'];
const SQUAD_COLORS = ['#3b82f6','#22c55e','#f59e0b','#8b5cf6','#ec4899','#14b8a6','#f97316','#6366f1'];
function squadColor(id) {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = String(id).charCodeAt(i) + ((h << 5) - h);
  return SQUAD_COLORS[Math.abs(h) % SQUAD_COLORS.length];
}

// ── Avatar ─────────────────────────────────────────────────────────────────────
function Avatar({ member }) {
  const initials = `${member.firstName?.[0]||''}${member.lastName?.[0]||''}`.toUpperCase();
  if (member.profileImage) {
    return (
      <img src={resolveMediaUrl(member.profileImage)} alt={initials}
        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
        onError={e => { e.target.onerror=null; e.target.src=`https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=3b82f6&color=fff&size=64`; }} />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-xs flex-shrink-0">
      {initials || '?'}
    </div>
  );
}

// ── Move-to dropdown ───────────────────────────────────────────────────────────
function MoveDropdown({ member, squads, onMove }) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${theme.border} ${theme.textSecondary} ${theme.cardHover} transition`}
      >
        Move <ChevronDown size={11} />
      </button>
      {open && (
        <div className={`absolute right-0 top-7 z-30 min-w-36 ${theme.card} border ${theme.border} rounded-lg shadow-xl py-1 overflow-hidden`}>
          {member.squadId != null && (
            <button
              onClick={() => { onMove(null); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs ${theme.textSecondary} ${theme.cardHover} flex items-center gap-2`}
            >
              <X size={11} className="text-red-400" /> Unassign
            </button>
          )}
          {squads.map(s => (
            s.linkId !== member.squadId && (
              <button key={s.linkId}
                onClick={() => { onMove(s.linkId); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs ${theme.text} ${theme.cardHover} flex items-center gap-2`}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: squadColor(s.groupId) }} />
                {s.name}
              </button>
            )
          ))}
        </div>
      )}
    </div>
  );
}

// ── Member card ────────────────────────────────────────────────────────────────
function MemberCard({ member, squads, onMove, highlighted }) {
  const { theme } = useTheme();
  const ageGroup = getAgeGroup(member.dob);
  return (
    <div className={`rounded-lg border px-3 py-2.5 flex items-center gap-2.5 transition-all ${
      highlighted
        ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 shadow-sm'
        : `${theme.card} ${theme.border}`
    }`}>
      <Avatar member={member} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold ${theme.text} truncate`}>
          {member.firstName} {member.lastName}
        </p>
        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
          {ageGroup && (
            <span className="text-[10px] px-1.5 py-px rounded-full bg-blue-100 text-blue-700 font-medium">{ageGroup}</span>
          )}
          {member.gender && (
            <span className={`text-[10px] px-1.5 py-px rounded-full font-medium ${
              member.gender.toLowerCase() === 'female'
                ? 'bg-pink-100 text-pink-700'
                : member.gender.toLowerCase() === 'male'
                  ? 'bg-sky-100 text-sky-700'
                  : 'bg-gray-100 text-gray-600'
            }`}>{member.gender.charAt(0).toUpperCase() + member.gender.slice(1)}</span>
          )}
        </div>
      </div>
      <MoveDropdown member={member} squads={squads} onMove={(squadId) => onMove(member.enrollmentId, squadId)} />
    </div>
  );
}

// ── Column ─────────────────────────────────────────────────────────────────────
function Column({ title, color, members, squads, onMove, highlightedIds, count, max }) {
  const { theme } = useTheme();
  const full = max != null && count >= max;
  return (
    <div className="flex flex-col flex-shrink-0" style={{ width: 240 }}>
      {/* Column header */}
      <div className={`flex items-center justify-between px-3 py-2.5 rounded-t-xl border-x border-t ${theme.border}`}
        style={{ borderTopColor: color, borderTopWidth: 3 }}>
        <div className="flex items-center gap-2 min-w-0">
          {color !== 'transparent' && (
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
          )}
          <span className={`text-xs font-bold ${theme.text} truncate`}>{title}</span>
        </div>
        <span className={`text-xs font-bold flex-shrink-0 ml-2 px-1.5 py-px rounded-full ${
          full ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
        }`}>
          {count}{max != null ? `/${max}` : ''}
        </span>
      </div>

      {/* Member list */}
      <div className={`flex-1 border-x border-b ${theme.border} rounded-b-xl p-2 space-y-1.5 overflow-y-auto`}
        style={{ minHeight: 120, maxHeight: 480 }}>
        {members.length === 0 ? (
          <div className="flex items-center justify-center h-20">
            <p className={`text-xs ${theme.textMuted} text-center`}>No members</p>
          </div>
        ) : members.map(m => (
          <MemberCard key={m.enrollmentId} member={m} squads={squads} onMove={onMove}
            highlighted={highlightedIds?.has(m.enrollmentId)} />
        ))}
      </div>
    </div>
  );
}

// ── Smart Assign Panel ─────────────────────────────────────────────────────────

const RULE_FIELDS = [
  { value: 'ageGroup', label: 'Age Group',   values: AGE_GROUPS },
  { value: 'gender',   label: 'Gender',      values: ['Male', 'Female', 'Other'] },
  { value: 'lastNameStartsWith', label: 'Last name starts with', values: 'text' },
];

function RuleRow({ rule, squads, onChange, onRemove, index }) {
  const { theme } = useTheme();
  const field = RULE_FIELDS.find(f => f.value === rule.field) || RULE_FIELDS[0];

  return (
    <div className={`flex items-center gap-2 p-2.5 rounded-lg border ${theme.border} ${theme.card}`}>
      <span className={`text-xs font-bold ${theme.textMuted} w-4 flex-shrink-0`}>{index + 1}</span>

      {/* Field */}
      <select value={rule.field} onChange={e => onChange({ ...rule, field: e.target.value, value: '' })}
        className={`text-xs border ${theme.border} ${theme.input} rounded px-2 py-1 flex-shrink-0`} style={{ width: 130 }}>
        {RULE_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>

      <span className={`text-xs ${theme.textMuted} flex-shrink-0`}>is</span>

      {/* Value */}
      {field.values === 'text' ? (
        <input type="text" value={rule.value} onChange={e => onChange({ ...rule, value: e.target.value })}
          placeholder="e.g. A" maxLength={3}
          className={`text-xs border ${theme.border} ${theme.input} rounded px-2 py-1 w-16 flex-shrink-0`} />
      ) : (
        <select value={rule.value} onChange={e => onChange({ ...rule, value: e.target.value })}
          className={`text-xs border ${theme.border} ${theme.input} rounded px-2 py-1 flex-shrink-0`} style={{ width: 80 }}>
          <option value="">Pick…</option>
          {field.values.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      )}

      <ArrowRight size={12} className={`${theme.textMuted} flex-shrink-0`} />

      {/* Target squad */}
      <select value={rule.squadId ?? ''} onChange={e => onChange({ ...rule, squadId: e.target.value ? Number(e.target.value) : null })}
        className={`text-xs border ${theme.border} ${theme.input} rounded px-2 py-1 flex-1 min-w-0`}>
        <option value="">Squad…</option>
        {squads.map(s => <option key={s.linkId} value={s.linkId}>{s.name}</option>)}
      </select>

      <button onClick={onRemove} className="text-red-400 hover:text-red-600 flex-shrink-0">
        <X size={14} />
      </button>
    </div>
  );
}

function applyRules(members, rules, onlyUnassigned) {
  // Returns Map<enrollmentId, squadId>
  const result = new Map();
  const candidates = onlyUnassigned ? members.filter(m => m.squadId == null) : members;

  for (const member of candidates) {
    for (const rule of rules) {
      if (!rule.field || !rule.value || rule.squadId == null) continue;

      let match = false;
      if (rule.field === 'ageGroup') {
        match = getAgeGroup(member.dob) === rule.value;
      } else if (rule.field === 'gender') {
        match = (member.gender || '').toLowerCase() === rule.value.toLowerCase();
      } else if (rule.field === 'lastNameStartsWith') {
        match = (member.lastName || '').toLowerCase().startsWith(rule.value.toLowerCase());
      }

      if (match) {
        result.set(member.enrollmentId, rule.squadId); // last match wins
      }
    }
  }
  return result;
}

function SmartAssignPanel({ members, squads, onApply, onClose }) {
  const { theme } = useTheme();
  const [rules, setRules] = useState([{ field: 'ageGroup', value: '', squadId: null }]);
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);
  const [preview, setPreview] = useState(null); // Map<enrollmentId, squadId> | null
  const [applying, setApplying] = useState(false);

  const addRule = () => setRules(r => [...r, { field: 'ageGroup', value: '', squadId: null }]);
  const updateRule = (i, r) => setRules(prev => prev.map((x, idx) => idx === i ? r : x));
  const removeRule = (i) => setRules(prev => prev.filter((_, idx) => idx !== i));

  const handlePreview = () => {
    const result = applyRules(members, rules, onlyUnassigned);
    setPreview(result);
  };

  const handleApply = async () => {
    const toApply = preview || applyRules(members, rules, onlyUnassigned);
    if (toApply.size === 0) { toast('No members matched any rules'); return; }
    setApplying(true);
    try {
      await onApply([...toApply.entries()].map(([enrollmentId, squadId]) => ({ enrollmentId, squadId })));
      onClose();
    } finally {
      setApplying(false);
    }
  };

  // Preview stats
  const previewStats = useMemo(() => {
    if (!preview) return null;
    const bySquad = {};
    for (const [, squadId] of preview) {
      bySquad[squadId] = (bySquad[squadId] || 0) + 1;
    }
    return bySquad;
  }, [preview]);

  return (
    <div className={`${theme.card} border ${theme.border} rounded-xl overflow-hidden shadow-xl`}
      style={{ width: 480 }}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${theme.border}`}>
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-amber-500" />
          <span className={`font-bold text-sm ${theme.text}`}>Smart Assign</span>
        </div>
        <button onClick={onClose} className={`p-1 rounded ${theme.cardHover}`}><X size={16} /></button>
      </div>

      <div className="p-4 space-y-3">
        <p className={`text-xs ${theme.textMuted}`}>
          Build rules below. Rules run top to bottom — last match wins.
        </p>

        {/* Rules */}
        <div className="space-y-2">
          {rules.map((rule, i) => (
            <RuleRow key={i} index={i} rule={rule} squads={squads}
              onChange={r => updateRule(i, r)} onRemove={() => removeRule(i)} />
          ))}
        </div>

        <button onClick={addRule}
          className={`flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600 font-medium transition`}>
          <Plus size={13} /> Add rule
        </button>

        {/* Only unassigned toggle */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <div onClick={() => setOnlyUnassigned(v => !v)}
            className={`w-8 h-4 rounded-full transition-colors flex-shrink-0 relative ${onlyUnassigned ? 'bg-blue-500' : 'bg-gray-300'}`}>
            <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${onlyUnassigned ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </div>
          <span className={`text-xs ${theme.textSecondary}`}>Only reassign unassigned members</span>
        </label>

        {/* Preview results */}
        {previewStats && (
          <div className={`rounded-lg border ${theme.border} p-3 space-y-1`}>
            <p className={`text-xs font-semibold ${theme.text} mb-2`}>Preview — {preview.size} member{preview.size !== 1 ? 's' : ''} will move</p>
            {Object.entries(previewStats).map(([squadId, count]) => {
              const squad = squads.find(s => s.linkId === Number(squadId));
              return (
                <div key={squadId} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: squadColor(squad?.groupId) }} />
                  <span className={`text-xs ${theme.textSecondary}`}>{squad?.name || '?'}</span>
                  <span className={`text-xs font-bold ${theme.text} ml-auto`}>{count}</span>
                </div>
              );
            })}
            {members.length - preview.size > 0 && (
              <div className={`flex items-center gap-2 text-xs ${theme.textMuted} pt-1 border-t ${theme.border}`}>
                <AlertCircle size={11} />
                {members.length - preview.size} member{members.length - preview.size !== 1 ? 's' : ''} unaffected
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className={`flex gap-2 px-4 py-3 border-t ${theme.border}`}>
        <button onClick={handlePreview}
          className={`flex-1 px-3 py-2 text-xs font-semibold border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg transition`}>
          Preview
        </button>
        <button onClick={handleApply} disabled={applying}
          className="flex-1 px-3 py-2 text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-1.5">
          {applying ? <RefreshCw size={12} className="animate-spin" /> : <Check size={12} />}
          Apply All
        </button>
      </div>
    </div>
  );
}

// ── Main board ─────────────────────────────────────────────────────────────────
export default function ProgramRosterBoard({ program, academyId }) {
  const { theme } = useTheme();

  const [enrollments, setEnrollments] = useState([]);
  const [squads, setSquads]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [showSmartAssign, setShowSmartAssign] = useState(false);
  const [previewIds, setPreviewIds]   = useState(null); // Set<enrollmentId>

  // local squad assignment map: enrollmentId → squadId (int|null)
  // derived from enrollments, updated optimistically on move
  const [assignments, setAssignments] = useState({});

  useEffect(() => {
    if (!academyId || !program?.id) return;
    setLoading(true);
    Promise.all([
      academyAPI.enrollments.list(academyId, program.id),
      academyAPI.squads.list(academyId, program.id),
    ]).then(([enrs, sqs]) => {
      const enrList = Array.isArray(enrs) ? enrs : [];
      const sqList  = Array.isArray(sqs)  ? sqs  : [];
      setEnrollments(enrList);
      setSquads(sqList);
      // Seed local assignment map from API data
      const map = {};
      for (const e of enrList) map[e.enrollmentId] = e.squadId ?? null;
      setAssignments(map);
    }).catch(() => toast.error('Failed to load roster'))
      .finally(() => setLoading(false));
  }, [academyId, program?.id]);

  // Members with current local squad assignment merged in
  const members = useMemo(() =>
    enrollments.map(e => ({ ...e, squadId: assignments[e.enrollmentId] ?? null })),
    [enrollments, assignments]
  );

  // Grouped by squad
  const unassigned = useMemo(() => members.filter(m => m.squadId == null), [members]);
  const bySquad = useMemo(() => {
    const map = {};
    for (const s of squads) map[s.linkId] = [];
    for (const m of members) {
      if (m.squadId != null && map[m.squadId]) map[m.squadId].push(m);
    }
    return map;
  }, [members, squads]);

  const handleMove = async (enrollmentId, newSquadId) => {
    // Optimistic update
    setAssignments(prev => ({ ...prev, [enrollmentId]: newSquadId }));
    setSaving(true);
    try {
      await academyAPI.squads.bulkAssign(academyId, program.id, [{ enrollmentId, squadId: newSquadId }]);
    } catch (err) {
      // Roll back
      const member = members.find(m => m.enrollmentId === enrollmentId);
      setAssignments(prev => ({ ...prev, [enrollmentId]: member?.squadId ?? null }));
      toast.error(err?.message || 'Failed to move member');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkApply = async (assignmentList) => {
    // Optimistic
    const patch = {};
    for (const a of assignmentList) patch[a.enrollmentId] = a.squadId;
    setAssignments(prev => ({ ...prev, ...patch }));
    setSaving(true);
    try {
      await academyAPI.squads.bulkAssign(academyId, program.id, assignmentList);
      toast.success(`${assignmentList.length} member${assignmentList.length !== 1 ? 's' : ''} assigned`);
      setPreviewIds(null);
    } catch (err) {
      // Roll back
      const rollback = {};
      for (const a of assignmentList) rollback[a.enrollmentId] = members.find(m => m.enrollmentId === a.enrollmentId)?.squadId ?? null;
      setAssignments(prev => ({ ...prev, ...rollback }));
      toast.error(err?.message || 'Failed to apply assignments');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-7 h-7 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (squads.length === 0) {
    return (
      <div className={`text-center py-16 ${theme.card} border ${theme.border} rounded-xl`}>
        <Users size={36} className={`mx-auto mb-3 ${theme.textMuted} opacity-30`} />
        <p className={`font-semibold ${theme.text}`}>No squads yet</p>
        <p className={`text-sm ${theme.textMuted} mt-1`}>Create squads first, then use the board to assign members.</p>
      </div>
    );
  }

  const totalEnrolled  = members.length;
  const totalUnassigned = unassigned.length;

  return (
    <div>
      {/* ── Top bar ── */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`text-sm font-semibold ${theme.text}`}>{totalEnrolled} enrolled</span>
          {totalUnassigned > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">
              {totalUnassigned} unassigned
            </span>
          )}
          {totalUnassigned === 0 && totalEnrolled > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold flex items-center gap-1">
              <Check size={11} /> All assigned
            </span>
          )}
          {saving && (
            <span className={`text-xs ${theme.textMuted} flex items-center gap-1`}>
              <RefreshCw size={11} className="animate-spin" /> Saving…
            </span>
          )}
        </div>

        <button
          onClick={() => setShowSmartAssign(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
            showSmartAssign
              ? 'bg-amber-500 text-white'
              : 'bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100'
          }`}
        >
          <Zap size={14} /> Smart Assign
        </button>
      </div>

      {/* ── Smart Assign panel ── */}
      {showSmartAssign && (
        <div className="mb-5">
          <SmartAssignPanel
            members={members}
            squads={squads}
            onApply={handleBulkApply}
            onClose={() => setShowSmartAssign(false)}
          />
        </div>
      )}

      {/* ── Kanban board ── */}
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ alignItems: 'flex-start' }}>
        {/* Unassigned column */}
        <Column
          title="Unassigned"
          color="transparent"
          members={unassigned}
          squads={squads}
          onMove={handleMove}
          highlightedIds={previewIds}
          count={unassigned.length}
          max={null}
        />

        {/* Squad columns */}
        {squads.map(squad => (
          <Column
            key={squad.linkId}
            title={squad.name}
            color={squadColor(squad.groupId)}
            members={bySquad[squad.linkId] || []}
            squads={squads}
            onMove={handleMove}
            highlightedIds={previewIds}
            count={(bySquad[squad.linkId] || []).length}
            max={null}
          />
        ))}
      </div>
    </div>
  );
}
